import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";

import type { Role } from "shell/platform/types/types";

import { featureFlags } from "shell/platform/config/featureFlags";
import {
  INTERNAL_EMAIL_DOMAIN,
  INTERNAL_ROLES,
  PORTAL_ROLES,
  ROLES_CLAIM_KEY,
} from "shell/platform/constants/constants";

export const MOCKED_ROLES = [
  "Manager",
  "Internal",
  "Organisation Admin",
  "SponsorGuard Read",
  "SponsorGuard Write",
  "SponsorGuard Approval",
  "SponsorGuard Freeze",
];

type IdToken = {
  [ROLES_CLAIM_KEY]: Array<Role> | undefined;
};

/**
 * When using the VITE_ACCESS_TOKEN_OVERRIDE variable we need to mock the roles implementation
 * as the "Thredd Roles" claim can not be obtainened from the ID token.
 *
 * @returns {Object} An object containing:
 *   - roles: Array<Role> - List of current user's roles
 *   - isLoading: boolean - Loading state of authentication
 *   - hasNoRoles: boolean - True if user has no assigned roles
 *   - hasRole: (current: Array<Role>) => boolean - Function to check if user has any of the specified roles
 */
export const useMockedRoles = () => {
  return {
    roles: MOCKED_ROLES,
    isLoading: false,
    hasNoRoles: false,
    hasInvalidInternalRoleDomain: false,
    hasRole: (role: Array<Role>) => role.some(r => MOCKED_ROLES.includes(r)),
    hasPortalReadAccess: true,
    hasInternalAccess: true,
    hasPanFinderAccess: true,
  };
};

/**
 * A React hook that manages user role access based on JWT token claims.
 *
 * @returns {Object} An object containing:
 *   - roles: Array<Role> - List of current user's roles
 *   - isLoading: boolean - Loading state of authentication
 *   - hasNoRoles: boolean - True if user has no assigned roles
 *   - hasRole: (current: Array<Role>) => boolean - Function to check if user has any of the specified roles
 *
 * @example
 * const { roles, hasRole, isLoading } = useRoles();
 *
 * // Check for specific roles
 * if (hasRole(['Internal', 'Manager'])) {
 *   // User has either Internal or Manager role
 * }
 *
 * // Wait for roles to load
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * // Check available roles
 * console.log(roles); // ['Internal', 'Card Operations Specialist']
 */
export const useOIDCRoles = () => {
  const auth = useAuth();
  const { user, isLoading } = auth ?? { user: null, isLoading: false };

  const idToken = user?.id_token;
  const roles = useMemo(() => {
    if (!idToken) return [];
    const parsedToken = jwtDecode<IdToken>(idToken);

    return parsedToken[ROLES_CLAIM_KEY] ?? [];
  }, [idToken]);

  const hasRole = (current: Array<Role>) => {
    const currentSet = new Set(current);
    return roles.some(role => currentSet.has(role));
  };

  const hasInternalRole = hasRole(INTERNAL_ROLES);
  const userEmail = user?.profile?.email ?? "";
  const internalRoleEmail = import.meta.env.VITE_INTERNAL_ROLE_EMAIL;
  const hasInvalidInternalRoleDomain =
    hasInternalRole &&
    !isLoading &&
    !userEmail.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN) &&
    userEmail.toLowerCase() !== internalRoleEmail?.toLowerCase();

  const hasPortalReadAccess = hasRole(PORTAL_ROLES);

  const hasPanFinderAccess =
    featureFlags.enablePanFinder && hasRole(["Sensitive Information Manager"]);

  const hasInternalAccess = hasRole(["Internal", "Internal Read Only"]);

  return {
    roles,
    isLoading,
    hasNoRoles: roles.length === 0 && !isLoading,
    hasInvalidInternalRoleDomain,
    hasRole,
    hasPortalReadAccess,
    hasInternalAccess,
    hasPanFinderAccess,
  };
};

export const useRoles = import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE
  ? useMockedRoles
  : useOIDCRoles;
